<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

const props = defineProps({
  source: {
    type: Object,
    required: true,
    validator: value =>
      ['campaign', 'flow'].includes(value.type) &&
      typeof value.id === 'number' &&
      typeof value.title === 'string',
  },
});

const { t } = useI18n();
const route = useRoute();

const isCampaign = computed(() => props.source.type === 'campaign');

const iconClass = computed(() =>
  isCampaign.value ? 'i-lucide-megaphone' : 'i-lucide-git-branch'
);

const isSent = computed(() => props.source.origin === 'sent');

const startedText = computed(() => {
  if (isCampaign.value) {
    return isSent.value
      ? t('SOURCE_ORIGIN.CAMPAIGN_SENT')
      : t('SOURCE_ORIGIN.CAMPAIGN_STARTED');
  }
  return isSent.value
    ? t('SOURCE_ORIGIN.FLOW_SENT')
    : t('SOURCE_ORIGIN.FLOW_STARTED');
});

const viewLabel = computed(() =>
  isCampaign.value
    ? t('SOURCE_ORIGIN.VIEW_CAMPAIGN')
    : t('SOURCE_ORIGIN.VIEW_FLOW')
);

const linkTo = computed(() => {
  const accountId = route.params.accountId;
  if (isCampaign.value) {
    return {
      name: 'campaigns_whatsapp_index',
      params: { accountId },
      query: { highlight: String(props.source.id) },
    };
  }
  return {
    name: 'whatsapp_flow_edit',
    params: { accountId, flowId: String(props.source.id) },
  };
});
</script>

<template>
  <div
    class="flex flex-col gap-2 rounded-md border border-n-slate-6 bg-n-slate-3 p-2 text-xs mb-2"
  >
    <div class="flex items-start gap-1.5 text-n-slate-11 leading-snug">
      <span :class="iconClass" class="size-3.5 shrink-0 mt-px" />
      <span>
        {{ startedText }}
        <span class="font-semibold text-n-slate-12">{{ source.title }}</span>
      </span>
    </div>
    <p
      v-if="source.content"
      class="m-0 text-n-slate-12 leading-snug whitespace-pre-line"
    >
      {{ source.content }}
    </p>
    <router-link
      :to="linkTo"
      class="skip-context-menu flex items-center gap-1 text-n-brand hover:underline"
    >
      <span class="i-lucide-arrow-up-right size-3" />
      {{ viewLabel }}
    </router-link>
  </div>
</template>
